import { useMaximize } from "./hooks/useMaximize";
import React, { useState, useEffect } from "react";
import { supabase } from "./supabase";
import { logSupabaseError } from "./utils";
import "./App.css";

export default function Inventory({ onClose, user }) {
  const [tab, setTab] = useState("overview");
  const [adjustSeed, setAdjustSeed] = useState(null); // { productId }
  const { toggle, overlayClass, panelClass, icon } = useMaximize(true);

  return (
    <div className={overlayClass}>
      <div className={panelClass} style={{ maxWidth: "980px" }}>

        {/* Header */}
        <div className="report-header">
          <div>
            <h2 className="report-title">📊 Inventory Manager</h2>
            <p className="report-date">Warehouse stock · Shelf stock · Transfers</p>
          </div>
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <div className="inv-tabs-wrap">
              <div className="inv-tabs">
                {[
                  { key: "overview", label: "📈 Overview" },
                  { key: "transfer", label: "📤 To Shelf" },
                  { key: "adjust", label: "⚖️ Adjust" },
                  { key: "history", label: "📋 History" },
                ].map((t) => (
                  <button
                    key={t.key}
                    className={`inv-tab ${tab === t.key ? "active" : ""}`}
                    onClick={() => {
                      setTab(t.key);
                      if (t.key !== "adjust") setAdjustSeed(null);
                    }}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
            {/* Maximize button */}
            <button className="maximize-btn" onClick={toggle} title={icon === "⊞" ? "Maximize" : "Restore"}>
              {icon}
            </button>
          </div>
          <button className="report-close" onClick={onClose}>✕</button>
        </div>

        {tab === "overview" && <InventoryOverview />}
        {tab === "transfer" && (
          <TransferToShelf
            onContinueToAdjust={(productId) => {
              setAdjustSeed({ productId });
              setTab("adjust");
            }}
          />
        )}
        {tab === "adjust" && <AdjustStock user={user} seed={adjustSeed} />}
        {tab === "history" && <StockHistory />}
      </div>
    </div>
  );
}

// ══════════════════════════════════════
// TAB 1 — Overview (Warehouse + Shelf)
// ══════════════════════════════════════
function InventoryOverview() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => { fetchOverview(); }, []);

  async function fetchOverview() {
    // Fetch products and warehouse stock separately
    const [prodRes, wRes] = await Promise.all([
      supabase
        .from("products")
        .select("id, name, sku_code, barcode, category_code, stock_qty, low_stock_threshold, sale_unit, shelf_location")
        .eq("is_active", true)
        .order("name"),
      supabase
        .from("warehouse_stock")
        .select("product_id, quantity"),
    ]);

    logSupabaseError("Inventory: fetch products", prodRes.error);
    logSupabaseError("Inventory: fetch warehouse_stock", wRes.error);

    if (prodRes.data) {
      const wMap = {};
      if (wRes.data) wRes.data.forEach((w) => { wMap[w.product_id] = w.quantity; });

      const enriched = prodRes.data.map((p) => ({
        ...p,
        warehouse_qty: wMap[p.id] ?? 0,
      }));
      setItems(enriched);
    }
    setLoading(false);
  }

  const filtered = items.filter((p) =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    (p.sku_code ?? "").toLowerCase().includes(search.toLowerCase()) ||
    (p.barcode ?? "").includes(search)
  );

  const totalShelf = items.reduce((s, p) => s + Number(p.stock_qty), 0);
  const totalWarehouse = items.reduce((s, p) => s + Number(p.warehouse_qty ?? 0), 0);
  const lowStockCount = items.filter((p) => p.stock_qty <= p.low_stock_threshold && p.stock_qty > 0).length;
  const outOfStock = items.filter((p) => p.stock_qty <= 0).length;

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
      {/* Summary KPIs */}
      <div className="rpt-kpi-row inv-overview-kpi-row">
        <div className="rpt-kpi cyan">
          <p className="rpt-kpi-icon">🏪</p>
          <p className="rpt-kpi-label">Total on Shelf</p>
          <p className="rpt-kpi-value">{totalShelf}</p>
        </div>
        <div className="rpt-kpi amber">
          <p className="rpt-kpi-icon">🏭</p>
          <p className="rpt-kpi-label">Total Backstock (Warehouse)</p>
          <p className="rpt-kpi-value">{totalWarehouse}</p>
        </div>
        <div className="rpt-kpi" style={{ "--kpi-color": "var(--amber)" }}>
          <p className="rpt-kpi-icon">⚠️</p>
          <p className="rpt-kpi-label">Low Stock</p>
          <p className="rpt-kpi-value" style={{ color: "var(--amber)" }}>{lowStockCount}</p>
        </div>
        <div className="rpt-kpi">
          <p className="rpt-kpi-icon">❌</p>
          <p className="rpt-kpi-label">Out of Stock</p>
          <p className="rpt-kpi-value" style={{ color: "var(--red)" }}>{outOfStock}</p>
        </div>
      </div>

      {/* Search */}
      <div className="report-toolbar-row" style={{ padding: "12px 16px", borderBottom: "1px solid var(--border)" }}>
        <input
          className="products-search"
          placeholder="Search by name, SKU or barcode..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Table */}
      <div className="report-table-wrap">
        {loading ? (
          <p className="report-loading">Loading...</p>
        ) : (
          <table className="report-table">
            <thead>
              <tr>
                <th>SKU</th>
                <th>Product Name</th>
                <th>Barcode</th>
                <th>Cat. Code</th>
                <th>Unit</th>
                <th>🏭 Warehouse</th>
                <th>🏪 Shelf</th>
                <th>Status</th>
                <th>Shelf Location</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => {
                const warehouseQty = p.warehouse_qty;
                const shelfQty = p.stock_qty;
                const status =
                  shelfQty <= 0 ? "out"
                    : shelfQty <= p.low_stock_threshold ? "low"
                      : "ok";
                return (
                  <tr key={p.id}>
                    <td className="receipt-no">{p.sku_code ?? "—"}</td>
                    <td style={{ fontWeight: 600 }}>{p.name}</td>
                    <td style={{ fontFamily: "var(--mono)", fontSize: "12px", color: "var(--text3)" }}>
                      {p.barcode ?? "—"}
                    </td>
                    <td>
                      <span className="reason-badge">{p.category_code ?? "—"}</span>
                    </td>
                    <td style={{ color: "var(--text3)", fontSize: "12px" }}>{p.sale_unit}</td>
                    <td>
                      <span style={{ color: "var(--amber)", fontWeight: 700, fontFamily: "var(--mono)" }}>
                        {p.warehouse_qty}
                      </span>
                    </td>
                    <td>
                      <span className={
                        status === "out" ? "stock-out"
                          : status === "low" ? "stock-low"
                            : "stock-ok"
                      }>
                        {shelfQty}
                      </span>
                    </td>
                    <td>
                      {status === "out" && <span className="inv-out-badge">OUT</span>}
                      {status === "low" && <span className="inv-low-badge">LOW</span>}
                      {status === "ok" && <span style={{ color: "var(--green)", fontSize: "11px" }}>✓ OK</span>}
                    </td>
                    <td style={{ color: "var(--text2)", fontSize: "12px", fontFamily: "var(--mono)" }}>
                      {p.shelf_location ?? "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan="5" className="total-label">TOTAL ({filtered.length} products)</td>
                <td className="total-amount" style={{ color: "var(--amber)" }}>{totalWarehouse}</td>
                <td className="total-amount">{totalShelf}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════
// TAB 2 — Transfer Warehouse → Shelf
// ══════════════════════════════════════
function TransferToShelf({ onContinueToAdjust }) {
  const [warehouseStock, setWarehouseStock] = useState([]);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => { fetchWarehouseStock(); }, []);

  async function fetchWarehouseStock() {
    const { data, error } = await supabase
      .from("warehouse_stock")
      .select(`
        quantity,
        updated_at,
        products(id, name, sku_code, barcode, stock_qty, sale_unit, low_stock_threshold)
      `)
      .order("quantity", { ascending: false });
    logSupabaseError("Inventory: fetch warehouse_stock (TransferToShelf)", error);
    if (data) setWarehouseStock(data);
  }

  const filtered = warehouseStock.filter((w) =>
    (w.products?.name ?? "").toLowerCase().includes(search.toLowerCase()) ||
    (w.products?.sku_code ?? "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="inv-adjust-layout">
      {/* Left — Warehouse List */}
      <div className="inv-product-list">
        <div style={{ padding: "12px", borderBottom: "1px solid var(--border)" }}>
          <input
            className="products-search"
            placeholder="Search warehouse..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="inv-product-scroll">
          {filtered.length === 0 ? (
            <div style={{ padding: "24px", textAlign: "center", color: "var(--text3)", fontSize: "13px" }}>
              <p style={{ fontSize: "28px", marginBottom: "8px" }}>🏭</p>
              <p>No stock in warehouse</p>
              <p style={{ fontSize: "11px", marginTop: "4px" }}>Receive items first</p>
            </div>
          ) : filtered.map((w, i) => (
            <div
              key={i}
              className={`inv-product-item ${selected?.products?.id === w.products?.id ? "selected" : ""}`}
              onClick={() => { setSelected(w); }}
            >
              <div className="inv-product-info">
                <p className="inv-product-name">{w.products?.name}</p>
                <p className="inv-product-unit">
                  {w.products?.sku_code && (
                    <span style={{ color: "var(--cyan)", marginRight: "6px" }}>{w.products.sku_code}</span>
                  )}
                  Shelf: <span className="stock-ok">{w.products?.stock_qty}</span>
                </p>
              </div>
              <div style={{ textAlign: "right", flexShrink: 0 }}>
                <p style={{ color: "var(--amber)", fontWeight: 700, fontFamily: "var(--mono)", fontSize: "16px" }}>
                  {w.quantity}
                </p>
                <p style={{ fontSize: "10px", color: "var(--text3)" }}>in warehouse</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Right — Shortcut to Adjust */}
      <div className="inv-form-panel">
        {!selected ? (
          <div className="inv-empty-state">
            <p style={{ fontSize: "32px" }}>👈</p>
            <p>Select a product to add to shelf</p>
            <p style={{ fontSize: "11px", color: "var(--text3)", textAlign: "center", marginTop: "8px" }}>
              This uses the <strong>Adjust</strong> workflow so every shelf add is logged with a reason.
            </p>
          </div>
        ) : (
          <>
            <div className="inv-selected-product">
              <p className="inv-selected-name">{selected.products?.name}</p>
              {selected.products?.sku_code && (
                <p style={{ fontSize: "11px", color: "var(--cyan)", marginBottom: "10px", fontFamily: "var(--mono)" }}>
                  {selected.products.sku_code}
                  {selected.products?.barcode && ` · ${selected.products.barcode}`}
                </p>
              )}
              <div className="inv-selected-stats">
                <div className="inv-stat">
                  <span>🏭 Warehouse</span>
                  <strong style={{ color: "var(--amber)" }}>{selected.quantity}</strong>
                </div>
                <div className="inv-stat">
                  <span>🏪 On Shelf</span>
                  <strong className="stock-ok">{selected.products?.stock_qty}</strong>
                </div>
                <div className="inv-stat">
                  <span>Unit</span>
                  <strong>{selected.products?.sale_unit}</strong>
                </div>
              </div>
            </div>

            <div className="inv-form-field">
              <label>Next step</label>
              <div style={{ color: "var(--text2)", fontSize: "13px", lineHeight: 1.5 }}>
                Continue to <strong>Adjust → Add to Shelf</strong> to enter quantity and choose a reason (Restock, Correction, Damaged, Expired Return, Internal Use).
              </div>
            </div>

            <button
              className="inv-save-btn"
              onClick={() => onContinueToAdjust?.(selected.products?.id)}
              disabled={!selected?.products?.id}
              style={{ background: "var(--cyan)", color: "#000", boxShadow: "0 4px 16px rgba(50,212,224,0.25)", marginTop: "auto" }}
            >
              ⚖️ Continue in Adjust
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════
// TAB 3 — Manual Adjust (damage/loss)
// ══════════════════════════════════════
function AdjustStock({ user, seed }) {
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [adjType, setAdjType] = useState("remove");
  const [qty, setQty] = useState("");
  const [reason, setReason] = useState("damaged_removed");
  const [removeDestination, setRemoveDestination] = useState(null); // "warehouse" | "dispose" | null
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  const REMOVE_REASONS = [
    { value: "damaged_removed", label: "💔 Damage" },
    { value: "expired", label: "⏰ Expired" },
    { value: "lost", label: "🔍 Lost / Missing" },
    { value: "manual", label: "✏️ Manual Correction" },
  ];

  const ADD_REASONS = [
    { value: "restock", label: "📦 Restock" },
    { value: "correction", label: "✏️ Correction" },
    { value: "damaged", label: "💔 Damaged (returned to shelf)" },
    { value: "expired_return", label: "⏰ Expired Return" },
    { value: "internal_use", label: "🏷️ Internal Use" },
  ];

  useEffect(() => {
    async function loadProducts() {
      const { data, error } = await supabase
        .from("products")
        .select(`
          id, name, sku_code, stock_qty,
          low_stock_threshold, sale_unit
        `)
        .eq("is_active", true)
        .order("name");
      logSupabaseError("Inventory: AdjustStock load products", error);

      if (data) {
        // Fetch warehouse stock separately
        const { data: wStock, error: wStockError } = await supabase
          .from("warehouse_stock")
          .select("product_id, quantity");
        logSupabaseError("Inventory: AdjustStock load warehouse_stock", wStockError);

        const wMap = {};
        if (wStock) wStock.forEach((w) => { wMap[w.product_id] = w.quantity; });

        const enriched = data.map((p) => ({
          ...p,
          warehouse_qty: wMap[p.id] ?? 0,
        }));
        setProducts(enriched);
      }
    }
    loadProducts();
  }, []);

  useEffect(() => {
    if (!seed?.productId || products.length === 0) return;
    const found = products.find((p) => p.id === seed.productId);
    if (!found) return;
    setSelected(found);
    setAdjType("add");
    setReason("restock");
    setQty("");
  }, [seed?.productId, products]);

  const filtered = products.filter((p) =>
    p.name.toLowerCase().includes(search.toLowerCase())
  );
  async function handleAdjust() {
    if (!selected || !qty || Number(qty) <= 0) {
      alert("Please select a product and enter quantity!");
      return;
    }

    // Client-side pre-check using snapshot values — gives fast UX feedback.
    // The RPC will re-validate against live DB values before committing.
    if (adjType === "add") {
      const warehouseQty = selected.warehouse_qty ?? 0;
      if (warehouseQty <= 0) {
        alert(`❌ Cannot add to shelf!\n"${selected.name}" has 0 stock in warehouse.\n\nPlease receive stock in Warehouse first.`);
        return;
      }
      if (Number(qty) > warehouseQty) {
        alert(`❌ Cannot add ${qty} to shelf!\nOnly ${warehouseQty} available in warehouse.`);
        return;
      }
    }

    if (adjType === "remove" && Number(qty) > selected.stock_qty) {
      alert(`Cannot remove ${qty} — only ${selected.stock_qty} on shelf!`);
      return;
    }

    // Destination is chosen via the in-modal buttons below, not window.confirm().
    let returnToWarehouse = false;
    let removalReason = reason;

    if (adjType === "remove") {
      if (!removeDestination) {
        alert("Please choose where these units are going!");
        return;
      }
      returnToWarehouse = removeDestination === "warehouse";
      if (!returnToWarehouse && !reason) {
        alert("Please select a reason!");
        return;
      }
      removalReason = returnToWarehouse ? "returned_warehouse" : reason;
    }

    setSaving(true);

    try {
      let result;

      if (adjType === "add") {
        const { data, error } = await supabase.rpc("adjust_stock_add_to_shelf", {
          p_product_id: selected.id,
          p_qty: Number(qty),
          p_user_id: user.id,
          p_notes: null,
        });
        if (error) throw error;
        result = data;
      } else {
        const { data, error } = await supabase.rpc("adjust_stock_remove_from_shelf", {
          p_product_id: selected.id,
          p_qty: Number(qty),
          p_user_id: user.id,
          p_reason: removalReason,
          p_return_warehouse: returnToWarehouse,
          p_notes: null,
        });
        if (error) throw error;
        result = data;
      }

      const newShelf = result.new_shelf_qty;

      const msg = adjType === "remove"
        ? returnToWarehouse
          ? `✅ ${qty} units returned to warehouse!\n${selected.name}: shelf ${selected.stock_qty} → ${newShelf}`
          : `✅ ${qty} units removed from shelf (${reason}).\n${selected.name}: ${selected.stock_qty} → ${newShelf}`
        : `✅ ${qty} units moved to shelf!\n${selected.name}: ${selected.stock_qty} → ${newShelf}`;

      alert(msg);
      setSelected(null);
      setQty("");

      // Re-fetch after state is cleared
      const { data: products, error: productsError } = await supabase
        .from("products")
        .select("id, name, sku_code, stock_qty, low_stock_threshold, sale_unit")
        .eq("is_active", true)
        .order("name");
      logSupabaseError("Inventory: AdjustStock re-fetch products", productsError);

      if (products) {
        const { data: wStock, error: wStockError } = await supabase
          .from("warehouse_stock")
          .select("product_id, quantity");
        logSupabaseError("Inventory: AdjustStock re-fetch warehouse_stock", wStockError);
        const wMap = {};
        if (wStock) wStock.forEach((w) => { wMap[w.product_id] = w.quantity; });
        setProducts(products.map((p) => ({ ...p, warehouse_qty: wMap[p.id] ?? 0 })));
      }

    } catch (err) {
      alert("❌ Adjustment error: " + err.message);
    } finally {
      setSaving(false);
    }
  }

  // Ceiling for the current direction: warehouse qty when moving to shelf,
  // shelf qty when removing from shelf — mirrors the RPCs' own limits so
  // the UI can block obviously-invalid input before it round-trips.
  const maxQty = adjType === "add" ? (selected?.warehouse_qty ?? 0) : (selected?.stock_qty ?? 0);
  const overLimit = !!qty && Number(qty) > maxQty;

  return (
    <div className="inv-adjust-layout">
      <div className="inv-product-list">
        <div style={{ padding: "12px", borderBottom: "1px solid var(--border)" }}>
          <input className="products-search" placeholder="Search shelf stock..."
            value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="inv-product-scroll">
          {filtered.map((p) => (
            <div
              key={p.id}
              className={`inv-product-item ${selected?.id === p.id ? "selected" : ""}`}
              onClick={() => { setSelected(p); setQty(""); setRemoveDestination(null); }}
            >
              <div className="inv-product-info">
                <p className="inv-product-name">{p.name}</p>
                <p className="inv-product-unit" style={{ color: "var(--text3)" }}>
                  {p.sku_code ?? "no SKU"}
                </p>
              </div>
              <div style={{ textAlign: "right" }}>
                <p className={p.stock_qty <= 0 ? "stock-out" : p.stock_qty <= p.low_stock_threshold ? "stock-low" : "stock-ok"}
                  style={{ fontFamily: "var(--mono)", fontWeight: 700 }}>
                  {p.stock_qty}
                </p>
                <p style={{ fontSize: "10px", color: "var(--text3)" }}>on shelf</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="inv-form-panel">
        {!selected ? (
          <div className="inv-empty-state">
            <p style={{ fontSize: "32px" }}>👈</p>
            <p>Select a product to adjust</p>
            <p style={{ fontSize: "11px", color: "var(--text3)", textAlign: "center", marginTop: "8px" }}>
              Use this to remove from shelf (damage/expiry/loss) or add to shelf from warehouse (restock).<br />
              Every change is logged with a reason.
            </p>
          </div>
        ) : (
          <>
            <div className="inv-selected-product">
              <p className="inv-selected-name">{selected.name}</p>
              <div className="inv-selected-stats">
                <div className="inv-stat">
                  <span>🏪 On Shelf</span>
                  <strong className={selected.stock_qty <= 0 ? "stock-out" : "stock-ok"}>
                    {selected.stock_qty}
                  </strong>
                </div>
                <div className="inv-stat">
                  <span>🏭 Warehouse</span>
                  <strong style={{ color: "var(--amber)" }}>
                    {selected.warehouse_qty ?? 0}
                  </strong>
                </div>
                <div className="inv-stat">
                  <span>SKU</span>
                  <strong style={{ fontSize: "12px" }}>{selected.sku_code ?? "—"}</strong>
                </div>
              </div>
            </div>

            <div className="inv-type-toggle">
              <button
                className={`inv-type-btn remove ${adjType === "remove" ? "active" : ""}`}
                onClick={() => { setAdjType("remove"); setReason("damaged_removed"); setRemoveDestination(null); }}
              >
                － Remove from Shelf
              </button>
              <button
                className={`inv-type-btn add ${adjType === "add" ? "active" : ""}`}
                onClick={() => { setAdjType("add"); setReason("restock"); setRemoveDestination(null); }}
              >
                ＋ Add to Shelf
              </button>
            </div>

            <div className="inv-form-field">
              <label>Quantity</label>
              <input type="number" min="1" value={qty}
                onChange={(e) => setQty(e.target.value)}
                placeholder="Enter quantity..."
                className="inv-qty-input" />
              {overLimit && (
                <p style={{ color: "var(--red)", fontSize: "11px", marginTop: "4px" }}>
                  ⚠️ Only {maxQty} available {adjType === "add" ? "in warehouse" : "on shelf"} — max {maxQty}.
                </p>
              )}
            </div>

            {qty && Number(qty) > 0 && (
              <div className="inv-preview">
                <span style={{ color: "var(--text2)" }}>{selected.stock_qty}</span>
                <span className="inv-arrow">{adjType === "add" ? `＋${qty}` : `－${qty}`}</span>
                <span className={adjType === "add" ? "stock-ok" : "stock-low"}>
                  {adjType === "add"
                    ? selected.stock_qty + Number(qty)
                    : selected.stock_qty - Number(qty)}
                </span>
              </div>
            )}

            {adjType === "remove" && (
              <div className="inv-form-field">
                <label>Where are these units going?</label>
                <div className="inv-type-toggle">
                  <button
                    type="button"
                    className={`inv-type-btn add ${removeDestination === "warehouse" ? "active" : ""}`}
                    onClick={() => setRemoveDestination("warehouse")}
                  >
                    📦 Return to Warehouse
                  </button>
                  <button
                    type="button"
                    className={`inv-type-btn remove ${removeDestination === "dispose" ? "active" : ""}`}
                    onClick={() => setRemoveDestination("dispose")}
                  >
                    🗑️ Dispose / Write Off
                  </button>
                </div>
              </div>
            )}

            {(adjType === "add" || (adjType === "remove" && removeDestination === "dispose")) && (
              <div className="inv-form-field">
                <label>Reason</label>
                <select className="inv-select" value={reason} onChange={(e) => setReason(e.target.value)}>
                  {(adjType === "remove" ? REMOVE_REASONS : ADD_REASONS)
                    .map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
            )}

            <button
              className="inv-save-btn"
              onClick={handleAdjust}
              disabled={saving || !qty || overLimit || (adjType === "remove" && !removeDestination)}
              style={{
                background: adjType === "add" ? "var(--green)" : "var(--red)",
                boxShadow: adjType === "add"
                  ? "0 4px 16px rgba(50,215,75,0.25)"
                  : "0 4px 16px rgba(255,69,58,0.25)",
                marginTop: "auto",
              }}
            >
              {saving ? "Saving..." : adjType === "add"
                ? `＋ Add ${qty || "?"} to Shelf`
                : `－ Remove ${qty || "?"} from Shelf`}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════
// TAB 4 — Stock History
// ══════════════════════════════════════
function StockHistory() {
  const [history, setHistory] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [tab, setTab] = useState("transfers");
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  function formatReason(reason) {
    const r = (reason ?? "").toString().trim();
    const map = {
      // removals / adjustments
      damaged_removed: "💔 Damage",
      expired: "⏰ Expired",
      lost: "🔍 Lost / Missing",
      manual: "✏️ Manual Correction",
      returned_warehouse: "🏭 Returned to Warehouse",

      // adds to shelf (from warehouse)
      restock: "📦 Restock",
      correction: "✏️ Correction",
      damaged: "💔 Damaged (returned to shelf)",
      expired_return: "⏰ Expired Return",
      internal_use: "🏷️ Internal Use",
    };

    if (map[r]) return map[r];
    if (!r) return "—";
    return r
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }

  useEffect(() => { fetchHistory(); }, []);

  async function fetchHistory() {
    setLoading(true);
    const [adjRes, trnRes] = await Promise.all([
      supabase.from("stock_adjustments")
        .select(`id, qty_before, qty_change, qty_after, reason, created_at,
          products(name, sku_code),
          profiles(full_name)`)
        .order("created_at", { ascending: false }).limit(100),
      supabase.from("warehouse_transfers")
        .select(`id, quantity, notes, created_at,
          products(name, sku_code),
          transferred_by:profiles!warehouse_transfers_transferred_by_fkey(full_name)`)
        .order("created_at", { ascending: false }).limit(100),
    ]);
    logSupabaseError("Inventory: fetch stock_adjustments history", adjRes.error);
    logSupabaseError("Inventory: fetch warehouse_transfers history", trnRes.error);
    if (adjRes.data) setHistory(adjRes.data);
    if (trnRes.data) setTransfers(trnRes.data);
    setLoading(false);
  }

  const filteredHistory = history.filter((h) =>
    (h.products?.name ?? "").toLowerCase().includes(search.toLowerCase())
  );
  const filteredTransfers = transfers.filter((t) =>
    (t.products?.name ?? "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
      <div className="inv-history-toolbar">
        <div className="inv-history-tabs">
          <button className={`filter-tab ${tab === "transfers" ? "active" : ""}`} onClick={() => setTab("transfers")}>
            📤 Shelf Transfers ({transfers.length})
          </button>
          <button className={`filter-tab ${tab === "adjustments" ? "active" : ""}`} onClick={() => setTab("adjustments")}>
            ⚖️ Adjustments ({history.length})
          </button>
        </div>
        <div className="inv-history-search-wrap">
          <input className="products-search"
            placeholder="Search product..."
            value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>

      <div className="report-table-wrap">
        {loading ? (
          <p className="report-loading">Loading...</p>
        ) : tab === "transfers" ? (
          filteredTransfers.length === 0 ? (
            <div className="report-empty"><p>📤</p><p>No transfers yet</p></div>
          ) : (
            <table className="report-table">
              <thead>
                <tr>
                  <th>SKU</th>
                  <th>Product</th>
                  <th>Qty Moved</th>
                  <th>By</th>
                  <th>Notes</th>
                  <th>Date & Time</th>
                </tr>
              </thead>
              <tbody>
                {filteredTransfers.map((t) => (
                  <tr key={t.id}>
                    <td className="receipt-no">{t.products?.sku_code ?? "—"}</td>
                    <td style={{ fontWeight: 600 }}>{t.products?.name ?? "—"}</td>
                    <td>
                      <span className="adj-change positive">+{t.quantity}</span>
                    </td>
                    <td style={{ color: "var(--text2)" }}>{t.transferred_by?.full_name ?? "—"}</td>
                    <td style={{ color: "var(--text3)", fontSize: "12px" }}>{t.notes ?? "—"}</td>
                    <td className="time-col">
                      <div>{new Date(t.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</div>
                      <div style={{ fontSize: "11px", color: "var(--text3)" }}>
                        {new Date(t.created_at).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        ) : (
          filteredHistory.length === 0 ? (
            <div className="report-empty"><p>⚖️</p><p>No adjustments yet</p></div>
          ) : (
            <table className="report-table">
              <thead>
                <tr>
                  <th>SKU</th>
                  <th>Product</th>
                  <th>Before</th>
                  <th>Change</th>
                  <th>After</th>
                  <th>Reason</th>
                  <th>By</th>
                  <th>Date & Time</th>
                </tr>
              </thead>
              <tbody>
                {filteredHistory.map((h) => (
                  <tr key={h.id}>
                    <td className="receipt-no">{h.products?.sku_code ?? "—"}</td>
                    <td style={{ fontWeight: 600 }}>{h.products?.name ?? "—"}</td>
                    <td style={{ fontFamily: "var(--mono)", color: "var(--text2)" }}>{h.qty_before}</td>
                    <td>
                      <span className={`adj-change ${h.qty_change > 0 ? "positive" : "negative"}`}>
                        {h.qty_change > 0 ? "+" : ""}{h.qty_change}
                      </span>
                    </td>
                    <td style={{ fontFamily: "var(--mono)", fontWeight: 600 }}>{h.qty_after}</td>
                    <td><span className="reason-badge">{formatReason(h.reason)}</span></td>
                    <td style={{ color: "var(--text2)" }}>{h.profiles?.full_name ?? "—"}</td>
                    <td className="time-col">
                      <div>{new Date(h.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</div>
                      <div style={{ fontSize: "11px", color: "var(--text3)" }}>
                        {new Date(h.created_at).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        )}
      </div>
    </div>
  );
}